import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { marked } from 'marked';
import { TrashIcon } from '@heroicons/react/24/outline';
import apiClient from '../services/api';
import { ExportDropdownMenu } from '../components/ui/ExportDropdownMenu';

interface ConsultationTopic {
  id: string;
  name: string;
  description: string;
  icon: string;
}

interface LifeStage {
  id: string;
  name: string;
}

interface RiskTolerance {
  id: string;
  name: string;
  description: string;
}

interface ActionItem {
  priority: string;
  action: string;
  timeframe?: string;
  notes?: string;
}

interface Resource {
  title: string;
  url: string;
  source: string;
  description?: string;
}

interface ConsultationResponse {
  session_id: string;
  topic: string;
  guidance: string;
  key_recommendations: string[];
  action_items: ActionItem[];
  considerations: string[];
  when_to_consult_professional: string[];
  related_topics: string[];
  follow_up_suggestions: string[];
  resources: Resource[];
  disclaimer: string;
}

interface HistoryItem {
  session_id: string;
  topic: string;
  topic_name: string;
  situation_summary: string;
  timestamp: string;
}

const AIFinancialAdvisor: React.FC = () => {
  const { t } = useTranslation('aiAdvisor');

  // State for topics and options
  const [topics, setTopics] = useState<ConsultationTopic[]>([]);
  const [lifeStages, setLifeStages] = useState<LifeStage[]>([]);
  const [riskTolerances, setRiskTolerances] = useState<RiskTolerance[]>([]);

  // Consultation state
  const [selectedTopic, setSelectedTopic] = useState<string>('');
  const [situation, setSituation] = useState('');
  const [lifeStage, setLifeStage] = useState('');
  const [riskTolerance, setRiskTolerance] = useState('');
  const [goals, setGoals] = useState('');

  // Response state
  const [response, setResponse] = useState<ConsultationResponse | null>(null);
  const [followUpQuestion, setFollowUpQuestion] = useState('');
  const [followUpResponses, setFollowUpResponses] = useState<Array<{question: string, answer: string}>>([]);

  // History state
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [showHistory, setShowHistory] = useState(false);

  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [isFollowUpLoading, setIsFollowUpLoading] = useState(false);
  const [error, setError] = useState('');

  const responseRef = useRef<HTMLDivElement>(null);

  // Helper function to render markdown content safely
  const renderMarkdown = (content: string): string => {
    return marked(content, { async: false }) as string;
  };

  useEffect(() => {
    loadTopicsAndOptions();
    loadHistory();
  }, []);

  const loadTopicsAndOptions = async () => {
    try {
      const [topicsRes, stagesRes, riskRes] = await Promise.all([
        apiClient.get('/ai-advisor/topics'),
        apiClient.get('/ai-advisor/life-stages'),
        apiClient.get('/ai-advisor/risk-tolerances')
      ]);
      setTopics(topicsRes.data.topics || []);
      setLifeStages(stagesRes.data.life_stages || []);
      setRiskTolerances(riskRes.data.risk_tolerances || []);
    } catch (err) {
      console.error('Failed to load options:', err);
    }
  };

  const loadHistory = async () => {
    try {
      const res = await apiClient.get('/ai-advisor/history');
      setHistory(res.data.consultations || []);
    } catch (err) {
      console.error('Failed to load history:', err);
    }
  };

  const handleConsult = async () => {
    if (!selectedTopic || !situation.trim()) {
      setError(t('errors.topicAndSituationRequired'));
      return;
    }

    setIsLoading(true);
    setError('');
    setResponse(null);
    setFollowUpResponses([]);

    try {
      const res = await apiClient.post('/ai-advisor/consult', {
        topic: selectedTopic,
        situation: situation.trim(),
        life_stage: lifeStage || undefined,
        risk_tolerance: riskTolerance || undefined,
        goals: goals.trim() ? [goals.trim()] : undefined
      });

      setResponse(res.data);
      loadHistory();

      // Scroll to response
      setTimeout(() => {
        responseRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } catch (err: any) {
      setError(err.response?.data?.detail || t('errors.consultFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleFollowUp = async () => {
    if (!response || !followUpQuestion.trim()) return;

    setIsFollowUpLoading(true);
    setError('');

    try {
      const res = await apiClient.post('/ai-advisor/follow-up', {
        session_id: response.session_id,
        question: followUpQuestion.trim()
      });

      setFollowUpResponses([
        ...followUpResponses,
        { question: followUpQuestion, answer: res.data.response }
      ]);
      setFollowUpQuestion('');
    } catch (err: any) {
      setError(err.response?.data?.detail || t('errors.followUpFailed'));
    } finally {
      setIsFollowUpLoading(false);
    }
  };

  const handleDeleteHistory = async (sessionId: string) => {
    if (!window.confirm(t('confirmDeleteHistory'))) return;

    try {
      await apiClient.delete(`/ai-advisor/history/${sessionId}`);
      loadHistory();
    } catch (err) {
      console.error('Failed to delete history:', err);
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm(t('confirmClearAll'))) return;

    try {
      await apiClient.delete('/ai-advisor/history');
      setHistory([]);
    } catch (err) {
      console.error('Failed to clear history:', err);
    }
  };

  const startNewConsultation = () => {
    setSelectedTopic('');
    setSituation('');
    setLifeStage('');
    setRiskTolerance('');
    setGoals('');
    setResponse(null);
    setFollowUpResponses([]);
    setFollowUpQuestion('');
    setError('');
  };

  const getTopicName = (topicId: string) => {
    // Use translation key instead of backend's hardcoded English name
    const translatedName = t(`topics.${topicId}`, { defaultValue: '' });
    return translatedName || topicId;
  };

  const handleExport = async (format: 'word' | 'pdf' | 'text' | 'csv') => {
    if (!response || format === 'csv') return;

    // Map format to backend format
    const backendFormat = format === 'word' ? 'docx' : format === 'text' ? 'txt' : format;

    try {
      // Build content from response
      let content = `# ${t('title')}\n\n`;
      content += `## ${t('topic')}: ${getTopicName(response.topic)}\n\n`;
      content += `### ${t('situation')}\n${situation}\n\n`;
      content += `### ${t('overview')}\n${response.guidance}\n\n`;

      if (response.key_recommendations.length > 0) {
        content += `### ${t('recommendations')}\n`;
        response.key_recommendations.forEach((rec, i) => {
          content += `${i + 1}. ${rec}\n`;
        });
        content += '\n';
      }

      if (response.action_items.length > 0) {
        content += `### ${t('actionItems')}\n`;
        response.action_items.forEach((item, i) => {
          content += `${i + 1}. [${item.priority}] ${item.action}${item.timeframe ? ` (${item.timeframe})` : ''}\n`;
        });
        content += '\n';
      }

      if (response.considerations.length > 0) {
        content += `### ${t('considerations')}\n`;
        response.considerations.forEach((cons, i) => {
          content += `${i + 1}. ${cons}\n`;
        });
        content += '\n';
      }

      if (response.when_to_consult_professional.length > 0) {
        content += `### ${t('whenToConsult')}\n`;
        response.when_to_consult_professional.forEach((item, i) => {
          content += `${i + 1}. ${item}\n`;
        });
        content += '\n';
      }

      if (followUpResponses.length > 0) {
        content += `## ${t('followUpQuestions')}\n\n`;
        followUpResponses.forEach((fu, i) => {
          content += `**Q${i + 1}: ${fu.question}**\n${fu.answer}\n\n`;
        });
      }

      content += `\n---\n${t('disclaimer.text')}`;

      const res = await apiClient.post('/export/document', {
        content,
        title: `${t('title')} - ${getTopicName(response.topic)}`,
        format: backendFormat
      }, { responseType: 'blob' });

      const blob = new Blob([res.data], {
        type: backendFormat === 'pdf' ? 'application/pdf' :
              backendFormat === 'docx' ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' :
              'text/plain'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `financial-advisor-${response.topic}.${backendFormat}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
    }
  };

  const handlePrint = () => {
    if (!response) return;

    // Convert markdown content to HTML using marked
    const guidanceHtml = marked(response.guidance, { async: false }) as string;

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>${t('title')} - ${getTopicName(response.topic)}</title>
        <style>
          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            line-height: 1.6;
            max-width: 800px;
            margin: 0 auto;
            padding: 40px 20px;
            color: #333;
          }
          h1 { text-align: center; color: #2563eb; border-bottom: 2px solid #2563eb; padding-bottom: 10px; }
          h2 { color: #1d4ed8; margin-top: 24px; }
          h3 { color: #1e40af; margin-top: 16px; }
          .meta { text-align: center; color: #666; margin-bottom: 24px; }
          ul, ol { margin: 8px 0; padding-left: 24px; }
          li { margin-bottom: 8px; }
          p { margin: 8px 0; }
          strong { font-weight: 600; }
          em { font-style: italic; }
          code { background: #f3f4f6; padding: 2px 6px; border-radius: 4px; font-family: monospace; }
          pre { background: #f3f4f6; padding: 12px; border-radius: 8px; overflow-x: auto; }
          pre code { background: none; padding: 0; }
          blockquote { border-left: 4px solid #2563eb; margin: 16px 0; padding-left: 16px; color: #666; }
          .content h3 { font-size: 1.1em; margin-top: 20px; margin-bottom: 8px; }
          .content ul, .content ol { margin: 12px 0; }
          .content li { margin-bottom: 8px; }
          .recommendation { display: flex; align-items: flex-start; gap: 8px; margin-bottom: 8px; }
          .recommendation::before { content: "✓"; color: #22c55e; }
          .consideration { display: flex; align-items: flex-start; gap: 8px; margin-bottom: 8px; }
          .consideration::before { content: "⚠"; color: #eab308; }
          .action-item { margin-bottom: 8px; padding: 8px; background: #f0fdf4; border-radius: 4px; }
          .action-priority { font-weight: 600; color: #166534; }
          .follow-up { border-left: 4px solid #2563eb; padding-left: 16px; margin: 16px 0; }
          .follow-up-question { font-weight: 600; margin-bottom: 8px; }
          .disclaimer { margin-top: 24px; padding: 12px; background: #fef9c3; border-radius: 8px; font-size: 12px; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <h1>${t('title')}</h1>
        <div class="meta">
          <p>${t('topic')}: ${getTopicName(response.topic)}</p>
          <p>${new Date().toLocaleString()}</p>
        </div>

        <h2>${t('situation')}</h2>
        <p>${situation}</p>

        <h2>${t('overview')}</h2>
        <div class="content">${guidanceHtml}</div>

        ${response.key_recommendations.length > 0 ? `
          <h2>${t('recommendations')}</h2>
          <ul>
            ${response.key_recommendations.map(rec => `<li class="recommendation">${marked(rec, { async: false })}</li>`).join('')}
          </ul>
        ` : ''}

        ${response.action_items.length > 0 ? `
          <h2>${t('actionItems')}</h2>
          <ol>
            ${response.action_items.map(item => `<li class="action-item"><span class="action-priority">[${item.priority}]</span> ${item.action}${item.timeframe ? ` <em>(${item.timeframe})</em>` : ''}</li>`).join('')}
          </ol>
        ` : ''}

        ${response.considerations.length > 0 ? `
          <h2>${t('considerations')}</h2>
          <ul>
            ${response.considerations.map(cons => `<li class="consideration">${marked(cons, { async: false })}</li>`).join('')}
          </ul>
        ` : ''}

        ${response.when_to_consult_professional.length > 0 ? `
          <h2>${t('whenToConsult')}</h2>
          <ul>
            ${response.when_to_consult_professional.map(item => `<li>${item}</li>`).join('')}
          </ul>
        ` : ''}

        ${followUpResponses.length > 0 ? `
          <h2>${t('followUpQuestions')}</h2>
          ${followUpResponses.map((fu, i) => `
            <div class="follow-up">
              <p class="follow-up-question">Q${i + 1}: ${fu.question}</p>
              <div class="content">${marked(fu.answer, { async: false })}</div>
            </div>
          `).join('')}
        ` : ''}

        <div class="disclaimer">
          <strong>${t('disclaimer.title')}:</strong> ${t('disclaimer.text')}
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.onload = () => {
        printWindow.print();
      };
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          {t('title')}
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          {t('subtitle')}
        </p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-600 text-red-700 dark:text-red-300 rounded-lg">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Consultation Form */}
        <div className="lg:col-span-2 space-y-6">
          {!response ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
                {t('startConsultation')}
              </h2>

              {/* Topic Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  {t('selectTopic')} <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {topics.map(topic => (
                    <button
                      key={topic.id}
                      onClick={() => setSelectedTopic(topic.id)}
                      className={`p-4 rounded-lg border-2 text-left transition-all ${
                        selectedTopic === topic.id
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                          : 'border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-500'
                      }`}
                    >
                      <div className="text-2xl mb-2 text-gray-900 dark:text-white">{topic.icon}</div>
                      <div className="font-medium text-gray-900 dark:text-white text-sm">
                        {t(`topics.${topic.id}`, { defaultValue: topic.name })}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Situation */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('describeSituation')} <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={situation}
                  onChange={(e) => setSituation(e.target.value)}
                  placeholder={t('situationPlaceholder')}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Optional Context */}
              <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
                  {t('optionalContext')}
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Life Stage */}
                  <div>
                    <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                      {t('lifeStage')}
                    </label>
                    <select
                      value={lifeStage}
                      onChange={(e) => setLifeStage(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="">{t('selectLifeStage')}</option>
                      {lifeStages.map(stage => (
                        <option key={stage.id} value={stage.id}>{t(`lifeStages.${stage.id}`, { defaultValue: stage.name })}</option>
                      ))}
                    </select>
                  </div>

                  {/* Risk Tolerance */}
                  <div>
                    <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                      {t('riskTolerance')}
                    </label>
                    <select
                      value={riskTolerance}
                      onChange={(e) => setRiskTolerance(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="">{t('selectRiskTolerance')}</option>
                      {riskTolerances.map(risk => (
                        <option key={risk.id} value={risk.id}>{t(`riskTolerances.${risk.id}`, { defaultValue: risk.name })}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Financial Goals */}
                <div className="mt-4">
                  <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                    {t('financialGoals')}
                  </label>
                  <input
                    type="text"
                    value={goals}
                    onChange={(e) => setGoals(e.target.value)}
                    placeholder={t('goalsPlaceholder')}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>

              {/* Submit Button */}
              <button
                onClick={handleConsult}
                disabled={isLoading || !selectedTopic || !situation.trim()}
                className="w-full py-3 px-6 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    {t('consulting')}
                  </>
                ) : (
                  <>
                    <span>💬</span>
                    {t('getAdvice')}
                  </>
                )}
              </button>
            </div>
          ) : (
            /* Response Display */
            <div ref={responseRef} className="space-y-6">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                    {t('yourAdvice')}: {getTopicName(response.topic)}
                  </h2>
                  <div className="flex items-center gap-2">
                    {/* Export Dropdown Menu */}
                    <ExportDropdownMenu
                      onExport={handleExport}
                      onPrint={handlePrint}
                      showPrint={true}
                    />
                    {/* Delete/Clear Button */}
                    <button
                      onClick={startNewConsultation}
                      className="p-2 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      title={t('clearResult')}
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                    {/* New Consultation Link */}
                    <button
                      onClick={startNewConsultation}
                      className="text-blue-600 dark:text-blue-400 hover:underline text-sm ml-2"
                    >
                      {t('newConsultation')}
                    </button>
                  </div>
                </div>

                {/* Guidance/Overview */}
                <div className="mb-6">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    {t('overview')}
                  </h3>
                  <div
                    className="text-gray-700 dark:text-gray-300 prose prose-sm dark:prose-invert max-w-none prose-headings:text-gray-900 dark:prose-headings:text-white prose-strong:text-gray-900 dark:prose-strong:text-white prose-ul:my-2 prose-ol:my-2 prose-li:my-1 prose-p:mb-4 prose-p:mt-0"
                    dangerouslySetInnerHTML={{ __html: renderMarkdown(response.guidance) }}
                  />
                </div>

                {/* Key Recommendations */}
                {response.key_recommendations.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                      {t('recommendations')}
                    </h3>
                    <ul className="space-y-2">
                      {response.key_recommendations.map((rec, i) => (
                        <li key={i} className="flex items-start gap-2 text-gray-700 dark:text-gray-300">
                          <span className="text-green-500 mt-1 flex-shrink-0">✓</span>
                          <span dangerouslySetInnerHTML={{ __html: renderMarkdown(rec).replace(/<\/?p>/g, '') }} />
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Action Items */}
                {response.action_items.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                      {t('actionItems')}
                    </h3>
                    <ol className="space-y-2">
                      {response.action_items.map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-gray-700 dark:text-gray-300 p-2 bg-green-50 dark:bg-green-900/20 rounded">
                          <span className="font-semibold text-green-700 dark:text-green-400 flex-shrink-0">[{item.priority}]</span>
                          <span>
                            <span dangerouslySetInnerHTML={{ __html: renderMarkdown(item.action).replace(/<\/?p>/g, '') }} />
                            {item.timeframe && <em className="text-gray-500 dark:text-gray-400 ml-1">({item.timeframe})</em>}
                          </span>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}

                {/* Considerations */}
                {response.considerations.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                      {t('considerations')}
                    </h3>
                    <ul className="space-y-2">
                      {response.considerations.map((cons, i) => (
                        <li key={i} className="flex items-start gap-2 text-gray-700 dark:text-gray-300">
                          <span className="text-yellow-500 mt-1 flex-shrink-0">⚠</span>
                          <span dangerouslySetInnerHTML={{ __html: renderMarkdown(cons).replace(/<\/?p>/g, '') }} />
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* When to Consult Professional */}
                {response.when_to_consult_professional.length > 0 && (
                  <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <h3 className="text-lg font-medium text-blue-900 dark:text-blue-300 mb-2">
                      {t('whenToConsult')}
                    </h3>
                    <ul className="space-y-1">
                      {response.when_to_consult_professional.map((item, i) => (
                        <li key={i} className="text-blue-800 dark:text-blue-200">• {item}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Helpful Resources / Reference Links */}
                {response.resources && response.resources.length > 0 && (
                  <div className="mb-6 p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border border-indigo-200 dark:border-indigo-700">
                    <h3 className="text-lg font-medium text-indigo-900 dark:text-indigo-300 mb-3 flex items-center gap-2">
                      🔗 {t('helpfulResources', 'Helpful Resources')}
                    </h3>
                    <ul className="space-y-3">
                      {response.resources.map((resource, i) => (
                        <li key={i} className="flex flex-col">
                          <a
                            href={resource.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-indigo-700 dark:text-indigo-300 hover:text-indigo-900 dark:hover:text-indigo-100 font-medium hover:underline"
                          >
                            {resource.title}
                          </a>
                          <span className="text-xs text-indigo-600 dark:text-indigo-400">
                            {resource.source}
                          </span>
                          {resource.description && (
                            <span className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                              {resource.description}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Follow-up Responses */}
                {followUpResponses.length > 0 && (
                  <div className="mb-6 space-y-4">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                      {t('followUpQuestions')}
                    </h3>
                    {followUpResponses.map((fu, i) => (
                      <div key={i} className="border-l-4 border-blue-500 pl-4">
                        <p className="font-medium text-gray-900 dark:text-white mb-2">
                          Q: {fu.question}
                        </p>
                        <div
                          className="text-gray-700 dark:text-gray-300 prose prose-sm dark:prose-invert max-w-none prose-headings:text-gray-900 dark:prose-headings:text-white prose-strong:text-gray-900 dark:prose-strong:text-white prose-ul:my-2 prose-ol:my-2 prose-li:my-1 prose-p:mb-4 prose-p:mt-0"
                          dangerouslySetInnerHTML={{ __html: renderMarkdown(fu.answer) }}
                        />
                      </div>
                    ))}
                  </div>
                )}

                {/* Follow-up Input */}
                <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('askFollowUp')}
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={followUpQuestion}
                      onChange={(e) => setFollowUpQuestion(e.target.value)}
                      placeholder={t('followUpPlaceholder')}
                      onKeyPress={(e) => e.key === 'Enter' && handleFollowUp()}
                      className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                    <button
                      onClick={handleFollowUp}
                      disabled={isFollowUpLoading || !followUpQuestion.trim()}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg transition-colors"
                    >
                      {isFollowUpLoading ? t('sending') : t('send')}
                    </button>
                  </div>
                </div>

              </div>

              {/* Disclaimer */}
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4">
                <h4 className="font-medium text-yellow-800 dark:text-yellow-300 mb-1">
                  {t('disclaimer.title')}
                </h4>
                <p className="text-sm text-yellow-700 dark:text-yellow-400">
                  {t('disclaimer.text')}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Right Column - History */}
        <div className="lg:col-span-1">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('consultationHistory')}
              </h3>
              {history.length > 0 && (
                <button
                  onClick={handleClearAll}
                  className="text-red-600 dark:text-red-400 hover:underline text-sm"
                >
                  {t('clearAll')}
                </button>
              )}
            </div>

            {history.length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400 text-sm text-center py-8">
                {t('noHistory')}
              </p>
            ) : (
              <div className="space-y-3 max-h-[600px] overflow-y-auto">
                {history.map(item => (
                  <div
                    key={item.session_id}
                    className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 dark:text-white text-sm truncate">
                          {t(`topics.${item.topic}`, { defaultValue: item.topic_name })}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {item.situation_summary}
                        </p>
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                          {new Date(item.timestamp).toLocaleDateString()}
                        </p>
                      </div>
                      <button
                        onClick={() => handleDeleteHistory(item.session_id)}
                        className="text-gray-400 hover:text-red-500 dark:hover:text-red-400 ml-2"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Tips Card */}
          <div className="mt-6 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-300 mb-3">
              💡 {t('tips.title')}
            </h3>
            <ul className="space-y-2 text-sm text-blue-800 dark:text-blue-200">
              <li>• {t('tips.tip1')}</li>
              <li>• {t('tips.tip2')}</li>
              <li>• {t('tips.tip3')}</li>
              <li>• {t('tips.tip4')}</li>
            </ul>
          </div>
        </div>
      </div>

    </div>
  );
};

export default AIFinancialAdvisor;
